import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storableError } from '../../util/errors';
import {
  updatePasswordOnServer,
  verifyRecoveryOnServer,
} from '../../util/api';

const RECOVERY_TYPE = 'recovery';

/**
 * Exchange the OTP token for a recovery-scoped Supabase session. Runs
 * exactly once per page mount: the token is consumed on the first
 * call, so subsequent submits would fail if this re-fired.
 *
 * @param {{ tokenHash: string, type?: string }} args
 */
export const verifyRecoveryThunk = createAsyncThunk(
  'PasswordResetPage/verifyRecovery',
  async ({ tokenHash, type }, { rejectWithValue }) => {
    try {
      const result = await verifyRecoveryOnServer({
        token_hash: tokenHash,
        type: type || RECOVERY_TYPE,
      });
      return { email: result?.email || null };
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

/**
 * Set a new password against the recovery-scoped session established by
 * `verifyRecoveryThunk`. Safe to retry: failures (e.g. "new password
 * must be different from old") leave the recovery session intact.
 *
 * @param {{ newPassword: string }} args
 */
export const updatePasswordThunk = createAsyncThunk(
  'PasswordResetPage/updatePassword',
  async ({ newPassword }, { rejectWithValue }) => {
    try {
      await updatePasswordOnServer({ password: newPassword });
      return true;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

export const verifyRecovery = (tokenHash, type) => dispatch =>
  dispatch(verifyRecoveryThunk({ tokenHash, type }));

export const resetPassword = newPassword => dispatch =>
  dispatch(updatePasswordThunk({ newPassword }));

const passwordResetSlice = createSlice({
  name: 'PasswordResetPage',
  initialState: {
    verifyRecoveryInProgress: false,
    verifyRecoveryError: null,
    recoverySessionEstablished: false,
    recoveryEmail: null,
    resetPasswordInProgress: false,
    resetPasswordError: null,
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(verifyRecoveryThunk.pending, state => {
        state.verifyRecoveryInProgress = true;
        state.verifyRecoveryError = null;
        state.recoverySessionEstablished = false;
      })
      .addCase(verifyRecoveryThunk.fulfilled, (state, action) => {
        state.verifyRecoveryInProgress = false;
        state.recoverySessionEstablished = true;
        state.recoveryEmail = action.payload?.email || null;
      })
      .addCase(verifyRecoveryThunk.rejected, (state, action) => {
        state.verifyRecoveryInProgress = false;
        state.verifyRecoveryError = action.payload;
      })
      .addCase(updatePasswordThunk.pending, state => {
        state.resetPasswordInProgress = true;
        state.resetPasswordError = null;
      })
      .addCase(updatePasswordThunk.fulfilled, state => {
        state.resetPasswordInProgress = false;
      })
      .addCase(updatePasswordThunk.rejected, (state, action) => {
        state.resetPasswordInProgress = false;
        state.resetPasswordError = action.payload;
      });
  },
});

export default passwordResetSlice.reducer;
